import {
  FLOW_TOKEN_KEY,
  FLOW_TOKEN_REJECTED_MARKER,
} from 'constants/flowToken'

const FLOW_TOKEN_PREFIX = `${ FLOW_TOKEN_KEY }=`
const REJECTION_PREFIX = `${ FLOW_TOKEN_REJECTED_MARKER }=`

// A response status, or what stood in for one when no response arrived. The
// fragment is anybody's to write, so nothing else is taken as an outcome.
//
// Anybody can still put a well-formed rejection in a link, which sends whoever
// follows it to the session-expired page and files a report. That is accepted:
// a session kept in the refresh cookie survives it, and opening the dashboard
// again resumes it. Handing the outcome over in session storage instead would make it
// unforgeable from another site, but where storage is blocked a failed exchange
// would land on the sign-in page, which enterprise users cannot get past.
const REJECTION_OUTCOME = /^(\d{3}|network|timeout)$/


/**
 * Separates the one-time flow token, and the outcome of an exchange that the
 * flow token page made and lost, from the rest of a URL fragment.
 *
 * The token is read exactly as it appears, with no decoding, because it
 * arrives unencoded. Everything else in the fragment is left as it was,
 * including a rejection whose outcome is not one the flow token page sends.
 *
 * An empty token is no token.
 *
 * @param {String} hash - a URL fragment, with or without its leading `#`
 *
 * @returns {Object} with the shape { flowToken, hash, rejection }: the token,
 *   or `null` when the fragment carries none; the fragment without the token or
 *   the rejection, which keeps its leading `#` only when something remains; and
 *   the rejected exchange's outcome, or `null` when the fragment carries none
 */
export default function removeFlowToken(hash) {
  const parts = hash.replace(/^#/, '').split('&')

  const tokenPart = parts.find(isFlowTokenPart)
  const flowToken = tokenPart?.slice(FLOW_TOKEN_PREFIX.length) || null

  const rejectionPart = parts.find(isRejectionPart)
  const rejection = rejectionPart?.slice(REJECTION_PREFIX.length) || null

  const remainingParts = parts.filter((part) => part && !isFlowTokenPart(part) && !isRejectionPart(part))
  const remainingHash = ( remainingParts.length ) ? `#${ remainingParts.join('&') }` : ''

  return {
    flowToken,
    hash: remainingHash,
    rejection,
  }
}

function isFlowTokenPart(part) {
  return part.startsWith(FLOW_TOKEN_PREFIX)
}

function isRejectionPart(part) {
  const outcome = part.slice(REJECTION_PREFIX.length)

  return ( part.startsWith(REJECTION_PREFIX) && REJECTION_OUTCOME.test(outcome) )
}
